// @ts-nocheck
/**
 * Voice Blacklist — unified page combining v1's Add, Bulk-upload, and View
 * flows into a single screen. Preserves all three API contracts:
 *   - API_BLACKLIST_ADD  { mobile: Number, callCenterId: Number }
 *   - API_BLACKLIST_BULK_UPLOAD  FormData { blistFile }
 *   - API_BLACKLIST_LIST(userId) → list
 *   - API_BLACKLIST_REMOVE_CALL_CENTRE(id) { callCentreId }
 *     (single-CC delete falls through to API_BLACKLIST_DELETE(id))
 */
import { useState, useEffect, useRef } from 'react';
import { usePersistedState } from '@/lib/filters/persisted-state';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import {
  Ban,
  Upload,
  RefreshCw,
  Loader2,
  Trash2,
  Plus,
  FileUp,
  Search as SearchIcon,
} from 'lucide-react';
import Loader from '@/components/partials/components/Loader';
import { getApiCall, postApiCall } from '@/service/httpCall';
import { postApiMediaCall } from '@/service/postApiCall';
import { deleteApiCallWithHeader } from '@/service/deleteApiCall';
import {
  API_BLACKLIST_ADD,
  API_BLACKLIST_BULK_UPLOAD,
  API_BLACKLIST_LIST,
  API_BLACKLIST_DELETE,
  API_BLACKLIST_REMOVE_CALL_CENTRE,
  API_CALL_CENTRE_LIST,
} from '@/service/url';
import { getUser } from '@/utilities/setting';
import { Button } from '@/components/ui/button';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const formatDate = (val: any) => {
  if (!val) return '—';
  return new Date(val).toLocaleString();
};

const VoiceBlacklistPage = () => {
  const location = useLocation();
  const initialTab: 'view' | 'add' | 'bulk' = location.pathname.endsWith('/add')
    ? 'add'
    : location.pathname.endsWith('/bulk')
      ? 'bulk'
      : 'view';
  const [tab, setTab] = useState<'view' | 'add' | 'bulk'>(initialTab);
  const [data, setData] = useState<any[]>([]);
  const [callCentreMap, setCallCentreMap] = useState<any>({});
  const [callCentres, setCallCentres] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [search, setSearch] = usePersistedState('voice-blacklist/VoiceBlacklist:search', '');

  // Add form state
  const [mobile, setMobile] = useState('');
  const [addCallCentreId, setAddCallCentreId] = useState('');
  const [addSubmitting, setAddSubmitting] = useState(false);

  // Bulk form state
  const [file, setFile] = useState<any>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Delete modal state
  const [showModal, setShowModal] = useState(false);
  const [modalItem, setModalItem] = useState<any>(null);
  const [selectedCallCentreId, setSelectedCallCentreId] = useState('');
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const user = getUser();
      const [blacklistResult, ccResult]: any = await Promise.allSettled([
        getApiCall(API_BLACKLIST_LIST(user?.id), {}),
        getApiCall(API_CALL_CENTRE_LIST, {}),
      ]);

      if (blacklistResult.status === 'fulfilled') {
        setData(Array.isArray(blacklistResult.value) ? blacklistResult.value : []);
      } else {
        if (
          typeof blacklistResult.reason === 'string' &&
          blacklistResult.reason.trim()
        )
          toast.error(friendlyApiError(blacklistResult.reason) || 'Failed to load list');
        setData([]);
      }

      if (ccResult.status === 'fulfilled' && Array.isArray(ccResult.value)) {
        setCallCentres(ccResult.value);
        const map: any = {};
        ccResult.value.forEach((cc: any) => {
          map[String(cc.id)] = cc.name;
        });
        setCallCentreMap(map);
      }
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleMobileChange = (e: any) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 10);
    setMobile(val);
  };

  const handleAddSubmit = async (e: any) => {
    e.preventDefault();
    if (!mobile || mobile.length !== 10) {
      toast.error('Please enter a valid 10-digit mobile number');
      return;
    }
    if (!addCallCentreId) {
      toast.error('Please select a call center');
      return;
    }
    setAddSubmitting(true);
    try {
      const result: any = await postApiCall(API_BLACKLIST_ADD, {
        mobile: Number(mobile),
        callCenterId: Number(addCallCentreId),
      });
      const msg = result?.message || 'Number added to Blacklist successfully';
      toast.success(msg);
      setMobile('');
      setAddCallCentreId('');
      load();
    } catch (err: any) {
      toast.error(friendlyApiError(err) || 'Something went wrong');
    } finally {
      setAddSubmitting(false);
    }
  };

  const handleFileChange = (e: any) => {
    setFile(e.target.files[0] || null);
  };

  const handleBulkSubmit = async (e: any) => {
    e.preventDefault();
    if (!file) {
      toast.error('Please select a .txt file');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('blistFile', file);
      await postApiMediaCall(API_BLACKLIST_BULK_UPLOAD, formData);
      toast.success('Blacklist uploaded successfully');
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      load();
    } catch (err: any) {
      toast.error(friendlyApiError(err) || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteClick = (item: any) => {
    const ids = item.call_centre_ids
      ? item.call_centre_ids.split(',').map(String).filter(Boolean)
      : [];

    if (ids.length <= 1) {
      confirmDelete(item, ids[0] || null);
    } else {
      setModalItem(item);
      setSelectedCallCentreId(ids[0]);
      setShowModal(true);
    }
  };

  const confirmDelete = async (item: any, callCentreId: any) => {
    setDeleting(true);
    try {
      if (callCentreId) {
        await postApiCall(API_BLACKLIST_REMOVE_CALL_CENTRE(item.id), {
          callCentreId: Number(callCentreId),
        });
      } else {
        await deleteApiCallWithHeader(API_BLACKLIST_DELETE(item.id));
      }
      toast.success('Removed from Blacklist');
      setShowModal(false);
      load();
    } catch (err: any) {
      toast.error(friendlyApiError(err) || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  const handleModalConfirm = () => {
    if (!selectedCallCentreId) {
      toast.error('Please select a call center');
      return;
    }
    confirmDelete(modalItem, selectedCallCentreId);
  };

  const getCallCentreNames = (call_centre_ids: any) => {
    if (!call_centre_ids) return '—';
    return call_centre_ids
      .split(',')
      .map((id: any) => callCentreMap[id] || id)
      .filter(Boolean)
      .join(', ');
  };

  const getModalCallCentres = () => {
    if (!modalItem?.call_centre_ids) return [];
    return modalItem.call_centre_ids
      .split(',')
      .map(String)
      .filter(Boolean)
      .map((id: any) => ({ id, name: callCentreMap[id] || id }));
  };

  const filteredData = search.trim()
    ? data.filter((item) => {
        const q = search.trim().toLowerCase();
        return (
          String(item.mobile || '').includes(q) ||
          getCallCentreNames(item.call_centre_ids).toLowerCase().includes(q)
        );
      })
    : data;

  if (initialLoad && loading) return <Loader />;

  return (
    <FillPage>
      <PageHeader
        title="Voice Blacklist"
        description="Block numbers per call center, upload in bulk, and manage active entries."
        actions={
          <Button
            variant="outline"
            size="icon"
            onClick={load}
            disabled={loading}
            title="Refresh"
            aria-label="Refresh"
          >
            <RefreshCw className={loading ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
          </Button>
        }
      />

      <div className="mb-3 flex flex-wrap gap-2">
        {[
          {
            key: 'view',
            label: 'View blacklist',
            icon: Ban,
            count: data.length,
            tone: 'primary',
          },
          {
            key: 'add',
            label: 'Add number',
            icon: Plus,
            tone: 'rose',
          },
          {
            key: 'bulk',
            label: 'Bulk upload',
            icon: FileUp,
            tone: 'sky',
          },
        ].map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          const activeCls =
            t.tone === 'rose'
              ? 'border-rose-500 bg-rose-500 text-white shadow-md ring-2 ring-rose-500/25'
              : t.tone === 'sky'
                ? 'border-sky-500 bg-sky-500 text-white shadow-md ring-2 ring-sky-500/25'
                : 'border-primary bg-primary text-primary-foreground shadow-md ring-2 ring-primary/25';
          const idleCls =
            t.tone === 'rose'
              ? 'border-rose-200 bg-rose-50 text-rose-700 hover:border-rose-400 hover:bg-rose-100 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-300'
              : t.tone === 'sky'
                ? 'border-sky-200 bg-sky-50 text-sky-700 hover:border-sky-400 hover:bg-sky-100 dark:border-sky-900/60 dark:bg-sky-950/40 dark:text-sky-300'
                : 'border-primary/30 bg-primary/[0.06] text-primary hover:border-primary/50 hover:bg-primary/10';
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setTab(t.key as any)}
              className={
                'relative flex h-10 items-center gap-2 rounded-lg border-2 px-4 text-sm font-bold transition ' +
                (active ? activeCls : idleCls)
              }
            >
              <Icon className="h-4 w-4" />
              {t.label}
              {typeof t.count === 'number' && (
                <span
                  className={
                    'ml-1 inline-flex min-w-[22px] items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] font-bold ' +
                    (active ? 'bg-white/90 text-foreground' : 'bg-muted text-muted-foreground')
                  }
                >
                  {t.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {tab === 'add' && (
        <div
          className="rounded-2xl border border-border/60 bg-card p-5"
          style={{
            boxShadow:
              '6px 6px 14px rgba(163, 177, 198, 0.35), -6px -6px 14px rgba(255, 255, 255, 0.9)',
          }}
        >
          <div className="mb-3 flex items-center gap-3 border-b border-border/50 pb-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-rose-100 text-rose-700 ring-1 ring-inset ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300">
              <Ban className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-foreground">Add number to blacklist</h3>
              <p className="text-[11px] text-muted-foreground">
                Block one number for a specific call center.
              </p>
            </div>
          </div>

          <form onSubmit={handleAddSubmit}>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-foreground">
                  Call center <span className="text-destructive">*</span>
                </label>
                <select
                  value={addCallCentreId}
                  onChange={(e) => setAddCallCentreId(e.target.value)}
                  className="input-embossed h-10 w-full rounded-md px-3 text-sm"
                >
                  <option value="">Select…</option>
                  {callCentres.map((cc: any) => (
                    <option key={cc.id} value={cc.id}>
                      {cc.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-foreground">
                  Mobile number <span className="text-destructive">*</span>
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="10-digit mobile"
                  value={mobile}
                  onChange={handleMobileChange}
                  maxLength={10}
                  className="input-embossed h-10 w-full rounded-md px-3 font-mono text-sm tracking-wider"
                />
              </div>
            </div>
            <div className="mt-3 flex justify-end">
              <Button
                type="submit"
                disabled={addSubmitting}
                className="gap-1.5"
              >
                {addSubmitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Plus className="h-4 w-4" />
                )}
                {addSubmitting ? 'Adding…' : 'Add to blacklist'}
              </Button>
            </div>
          </form>
        </div>
      )}

      {tab === 'bulk' && (
        <div
          className="rounded-2xl border border-border/60 bg-card p-5"
          style={{
            boxShadow:
              '6px 6px 14px rgba(163, 177, 198, 0.35), -6px -6px 14px rgba(255, 255, 255, 0.9)',
          }}
        >
          <div className="mb-3 flex items-center gap-3 border-b border-border/50 pb-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-100 text-sky-700 ring-1 ring-inset ring-sky-200 dark:bg-sky-950/40 dark:text-sky-300">
              <FileUp className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-foreground">Bulk upload</h3>
              <p className="text-[11px] text-muted-foreground">
                One 10-digit number per line in a .txt file.
              </p>
            </div>
          </div>

          <form onSubmit={handleBulkSubmit}>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-foreground">
                Select file <span className="text-destructive">*</span>
              </label>
              <input
                type="file"
                accept=".txt,text/plain"
                ref={fileInputRef}
                onChange={handleFileChange}
                className="input-embossed h-10 w-full rounded-md px-2 py-1.5 text-xs file:mr-3 file:rounded-md file:border-0 file:bg-primary/10 file:px-3 file:py-1 file:text-xs file:font-semibold file:text-primary hover:file:bg-primary/15"
              />
              {file && (
                <p className="text-[11px] text-muted-foreground">
                  Selected: <span className="font-mono text-foreground">{file.name}</span>
                </p>
              )}
            </div>
            <div className="mt-3 flex justify-end">
              <Button
                type="submit"
                disabled={uploading || !file}
                className="gap-1.5"
              >
                {uploading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4" />
                )}
                {uploading ? 'Uploading…' : 'Upload file'}
              </Button>
            </div>
          </form>
        </div>
      )}

      {tab === 'view' && (
      <div
        className="rounded-2xl border border-border/60 bg-card p-4"
        style={{
          boxShadow:
            '6px 6px 14px rgba(163, 177, 198, 0.35), -6px -6px 14px rgba(255, 255, 255, 0.9)',
        }}
      >
        <div className="mb-3 flex flex-wrap items-center gap-2 border-b border-border/50 pb-3">
          <h3 className="text-sm font-bold text-foreground">
            Blacklisted numbers
            <span className="ml-2 inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary ring-1 ring-inset ring-primary/25">
              {data.length}
            </span>
          </h3>
          <div className="ml-auto relative w-full max-w-[280px]">
            <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search by mobile or call center…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input-embossed h-9 w-full rounded-md pl-8 pr-3 text-xs"
            />
          </div>
        </div>

        <div className="overflow-x-auto rounded-lg border border-border/60">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-primary/[0.06] text-[10px] uppercase tracking-wide text-muted-foreground">
                <th className="px-3 py-2 text-left font-semibold">ID</th>
                <th className="px-3 py-2 text-left font-semibold">Mobile Number</th>
                <th className="px-3 py-2 text-left font-semibold">Call Center</th>
                <th className="px-3 py-2 text-left font-semibold">Created At</th>
                <th className="px-3 py-2 text-right font-semibold">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-3 py-6 text-center text-sm text-muted-foreground"
                  >
                    <span className="inline-flex items-center gap-1.5">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Loading…
                    </span>
                  </td>
                </tr>
              ) : filteredData.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-3 py-8 text-center text-sm text-muted-foreground"
                  >
                    {search ? 'No numbers match your search.' : 'No blacklisted numbers.'}
                  </td>
                </tr>
              ) : (
                filteredData.map((item, idx) => (
                  <tr
                    key={item.id}
                    className={`border-t border-border/40 ${idx % 2 ? 'bg-muted/30' : ''}`}
                  >
                    <td className="px-3 py-2 font-mono text-[11px] text-muted-foreground">
                      {item.id}
                    </td>
                    <td className="px-3 py-2 font-mono text-sm tracking-wider text-foreground">
                      {item.mobile}
                    </td>
                    <td className="px-3 py-2 text-foreground">
                      {getCallCentreNames(item.call_centre_ids)}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {formatDate(item.created_at)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        title="Remove from blacklist"
                        onClick={() => handleDeleteClick(item)}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-rose-100 text-rose-700 ring-1 ring-inset ring-rose-200 transition hover:bg-rose-200 dark:bg-rose-950/40 dark:text-rose-300"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {/* Multi-call-center remove picker */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent>
          <DialogHeader className="border-b border-border/50 pb-3">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-rose-100 text-rose-700 ring-1 ring-inset ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300">
                <Ban className="h-5 w-5" />
              </div>
              <div>
                <DialogTitle className="text-base font-bold text-foreground">
                  Remove from Blacklist
                </DialogTitle>
                <p className="text-[11px] text-muted-foreground">
                  Choose which call center to unblock this number for.
                </p>
              </div>
            </div>
          </DialogHeader>

          <div className="space-y-1.5 py-2">
            <div className="rounded-lg border border-border/60 bg-muted/30 p-3">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                Mobile
              </div>
              <div className="font-mono text-lg font-semibold tracking-wider text-foreground">
                {modalItem?.mobile}
              </div>
            </div>

            <label className="mt-3 block text-xs font-semibold text-foreground">
              Call center
            </label>
            <select
              value={selectedCallCentreId}
              onChange={(e) => setSelectedCallCentreId(e.target.value)}
              className="input-embossed h-10 w-full rounded-md px-3 text-sm"
            >
              {getModalCallCentres().map((cc: any) => (
                <option key={cc.id} value={cc.id}>
                  {cc.name}
                </option>
              ))}
            </select>
          </div>

          <DialogFooter className="border-t border-border/50 pt-3">
            <Button
              variant="outline"
              onClick={() => setShowModal(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleModalConfirm}
              disabled={deleting}
              className="gap-1.5"
            >
              {deleting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
              {deleting ? 'Removing…' : 'Remove'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </FillPage>
  );
};

export default VoiceBlacklistPage;
